import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner } from "./Bits";
import { Eye, Save, RotateCcw, Send, FileText, Plus, X, Info } from "lucide-react";

const SYNTAX_EXAMPLES = [
  { code: "{{name}}", desc: "Variable einfügen" },
  { code: "{{#if message}}…{{/if}}", desc: "Block nur zeigen, wenn vorhanden" },
  { code: "{{#if phone}}{{phone}}{{else}}—{{/if}}", desc: "Mit Fallback" },
  { code: "{{#unless premium}}…{{/unless}}", desc: "Wenn NICHT vorhanden" },
];

export default function EmailTemplatesAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [previewJsonText, setPreviewJsonText] = useState("");
  const [previewJsonError, setPreviewJsonError] = useState("");
  const [showSyntaxHelp, setShowSyntaxHelp] = useState(false);
  const [livePreview, setLivePreview] = useState(false);

  // Auto-refresh preview when livePreview is on and form/json changes (debounced 600ms)
  useEffect(() => {
    if (!livePreview || !form) return;
    const t = setTimeout(() => { doPreview(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePreview, form?.subject, form?.title, form?.body_html, form?.cta_text, form?.cta_url, previewJsonText]);

  const load = (autoSelect = true) => {
    setLoading(true);
    api.get("/admin/email-templates").then((r) => {
      setList(r.data || []);
      if (autoSelect && !selected && r.data?.length) {
        setSelected(r.data[0].key);
      }
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  useEffect(() => {
    if (!selected) return;
    const tpl = list.find((t) => t.key === selected);
    if (tpl) {
      const next = {
        ...tpl,
        custom_variables: tpl.custom_variables || [],
        preview_variables: tpl.preview_variables || {},
      };
      setForm(next);
      setPreview(null);
      setPreviewJsonText(JSON.stringify(next.preview_variables || {}, null, 2));
      setPreviewJsonError("");
    }
  }, [selected, list]);

  const save = async () => {
    setSaving(true);
    try {
      let previewVars = form.preview_variables;
      if (previewJsonText.trim()) {
        try {
          previewVars = JSON.parse(previewJsonText);
        } catch {
          setPreviewJsonError("Ungültiges JSON");
          toast.error("Preview-JSON ist nicht gültig");
          setSaving(false);
          return;
        }
      } else {
        previewVars = {};
      }
      await api.put(`/admin/email-templates/${form.key}`, {
        name: form.name,
        description: form.description,
        subject: form.subject,
        title: form.title || "",
        body_html: form.body_html,
        cta_text: form.cta_text || "",
        cta_url: form.cta_url || "",
        custom_variables: form.custom_variables || [],
        preview_variables: previewVars,
      });
      toast.success("Template gespeichert");
      load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!confirm("Auf Standard zurücksetzen? Deine Änderungen gehen verloren.")) return;
    try {
      await api.post(`/admin/email-templates/${form.key}/reset`);
      load(false);
      toast.success("Zurückgesetzt");
    } catch {
      toast.error("Reset fehlgeschlagen");
    }
  };

  const doPreview = async () => {
    let body = {};
    if (previewJsonText.trim()) {
      try {
        body = { variables: JSON.parse(previewJsonText) };
      } catch {
        toast.error("Preview-JSON ist nicht gültig – nutze JSON-Format");
        return;
      }
    }
    try {
      const r = await api.post(`/admin/email-templates/${form.key}/preview`, body);
      setPreview(r.data);
    } catch {
      toast.error("Vorschau fehlgeschlagen");
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Bitte E-Mail eintragen");
    let extraVars = null;
    if (previewJsonText.trim()) {
      try { extraVars = JSON.parse(previewJsonText); } catch { toast.error("Preview-JSON ungültig"); return; }
    }
    setTesting(true);
    try {
      const payload = { to: testTo };
      if (extraVars) payload.variables = extraVars;
      const r = await api.post(`/admin/email-templates/${form.key}/test-send`, payload);
      if (r.data.ok || r.data.dry_run) {
        toast.success(r.data.dry_run ? "Dry-Run: keine echte Mail (RESEND_API_KEY fehlt)" : "Test-Mail gesendet!");
      } else {
        toast.error("Resend: " + (r.data.error || "Fehler"));
      }
    } catch {
      toast.error("Versand fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const addCustomVar = () => {
    setForm({ ...form, custom_variables: [...(form.custom_variables || []), { name: "", default_value: "", description: "" }] });
  };
  const updateCustomVar = (idx, key, val) => {
    const arr = [...(form.custom_variables || [])];
    arr[idx] = { ...arr[idx], [key]: val };
    setForm({ ...form, custom_variables: arr });
  };
  const removeCustomVar = (idx) => {
    const arr = [...(form.custom_variables || [])];
    arr.splice(idx, 1);
    setForm({ ...form, custom_variables: arr });
  };

  if (loading) return <Spinner />;
  if (!form) return <div className="text-sm opacity-70">Keine Templates vorhanden.</div>;

  const allVariables = [
    ...(form.variables || []),
    ...((form.custom_variables || []).map((cv) => cv.name).filter(Boolean)),
  ];

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6" data-testid="admin-email-templates">
      {/* Template list */}
      <div className="space-y-2">
        {list.map((t) => (
          <button
            key={t.key}
            onClick={() => setSelected(t.key)}
            className={`w-full text-left p-3 rounded-xl border transition-colors ${selected === t.key ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : "bg-white hover:bg-[var(--wp-cream)]"}`}
            style={selected !== t.key ? { borderColor: "var(--wp-border)" } : {}}
            data-testid={`tpl-tab-${t.key}`}
          >
            <div className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 shrink-0" /> {t.name || t.key}
            </div>
            <div className={`text-[11px] mt-1 ${selected === t.key ? "opacity-80" : "opacity-60"}`}>{t.key}</div>
          </button>
        ))}
      </div>

      {/* Editor */}
      <div className="space-y-4">
        <div className="plant-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="overline mb-1">Template</div>
              <h3 className="font-display text-2xl">{form.name || form.key}</h3>
              {form.description && <p className="text-xs opacity-70 mt-1">{form.description}</p>}
            </div>
            <button
              onClick={() => setShowSyntaxHelp(!showSyntaxHelp)}
              className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="tpl-syntax-toggle"
            >
              <Info className="w-3.5 h-3.5" /> Syntax-Hilfe
            </button>
          </div>

          {showSyntaxHelp && (
            <div className="mt-4 p-4 rounded-xl text-xs bg-[var(--wp-cream)]" data-testid="tpl-syntax-help">
              <div className="font-medium mb-2">Mini-Handlebars-Syntax:</div>
              {SYNTAX_EXAMPLES.map((s, i) => (
                <div key={i} className="flex items-start gap-3 py-1">
                  <code className="bg-white px-2 py-1 rounded border text-[11px] whitespace-pre" style={{ borderColor: "var(--wp-border)" }}>{s.code}</code>
                  <span className="opacity-80 mt-1">{s.desc}</span>
                </div>
              ))}
              <div className="text-[11px] mt-2 opacity-70">Verschachtelung wird unterstützt. Variablen ohne Wert werden zu leerem String.</div>
            </div>
          )}

          {allVariables.length > 0 && (
            <div className="mt-4">
              <div className="overline mb-2">Verfügbare Variablen</div>
              <div className="flex flex-wrap gap-1.5">
                {allVariables.map((v, i) => (
                  <code
                    key={`${v}-${i}`}
                    onClick={() => { navigator.clipboard?.writeText(`{{${v}}}`); toast.success(`{{${v}}} kopiert`); }}
                    className="text-[11px] px-2 py-1 rounded bg-[var(--wp-cream)] border cursor-pointer hover:bg-[var(--wp-primary)] hover:text-[var(--wp-cream)] transition-colors"
                    style={{ borderColor: "var(--wp-border)" }}
                    title="Klick zum Kopieren"
                  >
                    {"{{"}{v}{"}}"}
                  </code>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 space-y-3">
            <div>
              <label className="overline">Betreff (Subject)</label>
              <input
                value={form.subject || ""}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid="tpl-subject"
              />
            </div>
            <div>
              <label className="overline">Titel (große Überschrift)</label>
              <input
                value={form.title || ""}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid="tpl-title"
              />
            </div>
            <div>
              <label className="overline">Body (HTML)</label>
              <textarea
                value={form.body_html || ""}
                onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border font-mono text-xs min-h-[260px]"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid="tpl-body"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="overline">Button-Text (optional)</label>
                <input value={form.cta_text || ""} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="tpl-cta-text" />
              </div>
              <div>
                <label className="overline">Button-URL (optional)</label>
                <input value={form.cta_url || ""} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="tpl-cta-url" />
              </div>
            </div>
          </div>

          {/* Custom Variables */}
          <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--wp-border)" }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">Eigene Variablen</div>
                <p className="text-xs opacity-70 mt-1">Definiere zusätzliche Platzhalter mit Default-Werten – nutzbar als {"{{name}}"} oder in {"{{#if}}"}-Blöcken.</p>
              </div>
              <button onClick={addCustomVar} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="tpl-add-custom-var">
                <Plus className="w-3.5 h-3.5" /> Variable
              </button>
            </div>
            {(form.custom_variables || []).length === 0 && (
              <div className="text-xs opacity-50 italic">Keine eigenen Variablen definiert.</div>
            )}
            {(form.custom_variables || []).map((cv, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1.5fr_auto] gap-2 mb-2" data-testid={`custom-var-row-${i}`}>
                <input placeholder="name" value={cv.name} onChange={(e) => updateCustomVar(i, "name", e.target.value.replace(/\s/g, "_"))} className="px-3 py-2 rounded-lg border text-sm font-mono" style={{ borderColor: "var(--wp-border)" }} data-testid={`custom-var-name-${i}`} />
                <input placeholder="Default-Wert" value={cv.default_value} onChange={(e) => updateCustomVar(i, "default_value", e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} />
                <input placeholder="Beschreibung (optional)" value={cv.description} onChange={(e) => updateCustomVar(i, "description", e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} />
                <button onClick={() => removeCustomVar(i)} className="px-2 text-[var(--wp-toxic)] hover:bg-red-50 rounded-lg" data-testid={`custom-var-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {/* Preview Variables JSON */}
          <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--wp-border)" }}>
            <div className="overline mb-2">Preview- & Test-Daten (JSON)</div>
            <p className="text-xs opacity-70 mb-2">Werte für Vorschau & Test-Mail. Leer lassen für automatische Beispieldaten.</p>
            <textarea
              value={previewJsonText}
              onChange={(e) => { setPreviewJsonText(e.target.value); setPreviewJsonError(""); }}
              placeholder='{"name":"Anna","message":"…"}'
              className="w-full px-3 py-2 rounded-lg border font-mono text-xs min-h-[100px]"
              style={{ borderColor: previewJsonError ? "var(--wp-toxic)" : "var(--wp-border)" }}
              data-testid="tpl-preview-json"
            />
            {previewJsonError && <div className="text-xs mt-1" style={{ color: "var(--wp-toxic)" }}>{previewJsonError}</div>}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t" style={{ borderColor: "var(--wp-border)" }}>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="tpl-save"><Save className="w-4 h-4" /> {saving ? "Speichere…" : "Speichern"}</button>
            <button onClick={doPreview} className="btn-secondary" data-testid="tpl-preview"><Eye className="w-4 h-4" /> Vorschau</button>
            <label className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full border cursor-pointer" style={{ borderColor: "var(--wp-border)", backgroundColor: livePreview ? "var(--wp-cream)" : "transparent" }} data-testid="tpl-live-preview-toggle">
              <input type="checkbox" checked={livePreview} onChange={(e) => { setLivePreview(e.target.checked); if (e.target.checked) doPreview(); }} className="m-0" />
              Live-Vorschau
            </label>
            <button onClick={resetDefault} className="btn-secondary" data-testid="tpl-reset"><RotateCcw className="w-4 h-4" /> Zurücksetzen</button>
            <div className="flex items-center gap-2 ml-auto">
              <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@email.lu" className="px-3 py-2 rounded-full border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="tpl-test-to" />
              <button onClick={sendTest} disabled={testing || !testTo} className="btn-primary !py-2" data-testid="tpl-test-send"><Send className="w-4 h-4" /> Test-Mail</button>
            </div>
          </div>
        </div>

        {preview && (
          <div className="plant-card p-5" data-testid="tpl-preview-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">Vorschau · Betreff</div>
                <div className="font-medium">{preview.subject}</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-xs opacity-70 hover:opacity-100">Schließen</button>
            </div>
            <iframe
              srcDoc={preview.html}
              title="email-preview"
              className="w-full bg-white rounded-xl border"
              style={{ minHeight: 580, borderColor: "var(--wp-border)" }}
              data-testid="tpl-preview-iframe"
            />
          </div>
        )}
      </div>
    </div>
  );
}
